import { MonitorDetailSummaryComponent } from "@/routes/processes/[processId]/qpm/requirements/[requirementId]/_components/monitoring/monitor-summary-component";
import t from "@/lang/lang";
import { useRequirementData } from "@/services/backend/qpm/requirements/hooks/requirement-context";
import { Loader2 } from "lucide-react";
import { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { fMoney } from "@/shared/components/domain/qpm/inputs/number-input";

type MaterialAdditionOverviewRow = {
  key: string;
  name: string;
  amount: number;
  basePrice: number;
};

function MaterialAdditionOverview({
  additions,
}: {
  additions: MaterialAdditionOverviewRow[];
}) {
  return (
    <div className="mt-6 flex flex-col">
      <span className="mb-2 text-xl font-bold">{t("Material-Zusätze")}</span>
      <Table className="border">
        <TableHeader className="bg-secondary">
          <TableRow>
            <TableHead>{t("Zusatz")}</TableHead>
            <TableHead className="text-end">{t("Gesamtanzahl")}</TableHead>
            <TableHead className="text-end">{t("Einzelpreis")}</TableHead>
            <TableHead className="text-end">{t("Gesamtpreis")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {additions.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-muted-foreground">
                {t("Keine Material-Zusätze ausgewählt")}
              </TableCell>
            </TableRow>
          ) : (
            additions.map((addition) => (
              <TableRow key={addition.key}>
                <TableCell>{addition.name}</TableCell>
                <TableCell className="text-end">{addition.amount}</TableCell>
                <TableCell className="text-end">
                  {fMoney(addition.basePrice)} €
                </TableCell>
                <TableCell className="text-end">
                  {fMoney(addition.amount * addition.basePrice)} €
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export function TotalOverviewRoute() {
  const { requirement, isLoading } = useRequirementData();

  const materialAdditions = useMemo(() => {
    const additions = new Map<string, MaterialAdditionOverviewRow>();
    if (!requirement) {
      return [];
    }

    for (const monitor of requirement.selectedMonitors ?? []) {
      const monitorSnap = requirement.monitorSnaps[monitor.monitorId];
      if (!monitorSnap) {
        continue;
      }

      for (const selectedAddition of monitor.selectedAdditions ?? []) {
        const additional = monitorSnap.additionals?.find(
          (item) => item.id === selectedAddition.additionalId,
        );
        if (!additional) {
          continue;
        }

        const amount =
          selectedAddition.materialMode === "SEPARATE_MATERIAL"
            ? selectedAddition.separateAmount || 0
            : Math.max(
                (monitor.amount || 0) - (monitor.existingStockAmount || 0),
                0,
              );
        const key = additional.name || selectedAddition.additionalId;
        const existing = additions.get(key);
        if (existing) {
          existing.amount += amount;
          continue;
        }

        additions.set(key, {
          key,
          name: additional.name,
          amount,
          basePrice: additional.basePrice || 0,
        });
      }
    }

    return Array.from(additions.values()).sort((left, right) =>
      left.name.localeCompare(right.name),
    );
  }, [requirement]);

  if (isLoading) {
    return <Loader2 className="animate-spin" />;
  }

  if (!requirement) {
    return <div>{t("Fehler beim Laden der Anforderung!")}</div>;
  }

  const { monitorSummary } = requirement;

  return (
    <div>
      <MonitorDetailSummaryComponent
        monitorSummary={monitorSummary}
        monitorRecords={requirement.monitorSnaps}
      />
      <MaterialAdditionOverview additions={materialAdditions} />
    </div>
  );
}
